import { useMemo, useState } from 'react'
import {
  LocalizationProvider,
  DateTimePicker as MuiDateTimePicker,
  type DateTimePickerProps as MuiDateTimePickerProps,
} from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import type { DateTimeValidationError } from '@mui/x-date-pickers/models'

import { FilterProps } from './utils'
import { usePageParams } from '../../../utils'

/**
 * Date filter, setting both date and time
 * @param paramName - parameter name
 * @param label - input label to show
 */
export const DateTimePicker = ({
  paramName,
  label,
  minDate = new Date('2020-01-01'),
  minDateMessage,
  maxDate = new Date(),
  maxDateMessage,
}: FilterProps) => {
  const [params, setPageParams] = usePageParams()
  const value = params[paramName] ?? new Date().toISOString()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<DateTimeValidationError | null>(null)

  const handleChange: MuiDateTimePickerProps['onChange'] = (value) => {
    setPageParams({ [paramName]: value?.toISOString(), pageNumber: undefined })
  }

  const errorMessage = useMemo(() => {
    switch (error) {
      case 'disableFuture':
        return 'Future dates cannot be specified'
      case 'invalidDate':
        return 'Invalid date'
      case 'maxDate':
        return maxDateMessage ?? ''
      case 'minDate':
        return minDateMessage ?? ''
      default:
        return ''
    }
  }, [error, minDateMessage, maxDateMessage])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} key={`filter-${paramName}`}>
      <MuiDateTimePicker
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        label={label}
        format="yyyy-MM-dd hh:mm a"
        value={new Date(value)}
        onChange={handleChange}
        minDate={minDate}
        maxDate={maxDate}
        disableFuture
        onError={(newError) => setError(newError)}
        slotProps={{
          actionBar: {
            actions: ['clear', 'accept'],
          },
          textField: {
            onClick: () => setOpen(true),
            helperText: errorMessage,
          },
        }}
      />
    </LocalizationProvider>
  )
}
